import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Github, MapPin, ArrowUpRight } from 'iconoir-react';

const Contact: React.FC = () => {
  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Layered Background - adds depth */}
      <div className="absolute inset-0 bg-neutral-900" />

      {/* Subtle radial gradient for depth */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(74, 103, 65, 0.15) 0%, transparent 60%)',
        }}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <span className="text-neutral-500 text-sm font-mono tracking-wide">07 — CONTACT</span>
            <h2 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-4 tracking-tight">
              Let's<br />
              <span className="text-matcha-500">Connect</span>
            </h2>
            <p className="text-lg text-neutral-400 max-w-md mx-auto lg:mx-0">
              Open to collaborate on scalable solutions and innovative projects.
            </p>

            {/* Location */}
            <div className="flex items-center justify-center lg:justify-start gap-2 mt-6 text-neutral-500">
              <MapPin width={16} height={16} />
              <span className="text-sm">Houston, Texas</span>
            </div>
          </motion.div>

          {/* Right: Contact Links - Redesigned with hierarchy */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4"
          >
            {/* Email - Primary/Featured */}
            <a
              href="mailto:anth.abid@gmail.com"
              className="group relative block overflow-hidden"
            >
              {/* Accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-matcha-500 rounded-full transform origin-bottom transition-transform duration-300 group-hover:scale-y-100 scale-y-0" />

              <div className="relative p-5 pl-6 bg-gradient-to-br from-neutral-800/80 to-neutral-800/40 rounded-2xl border border-neutral-700/50 transition-all duration-300 group-hover:border-matcha-500/30 group-hover:bg-neutral-800/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-matcha-500/10 rounded-xl border border-matcha-500/20 transition-all duration-300 group-hover:bg-matcha-500/20 group-hover:border-matcha-500/30">
                      <Mail className="text-matcha-400" width={22} height={22} />
                    </div>
                    <div>
                      <div className="text-[10px] text-matcha-400 font-mono uppercase tracking-widest mb-1">Email</div>
                      <div className="text-white font-medium text-lg">anth.abid@gmail.com</div>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-neutral-800/50 transition-all duration-300 group-hover:bg-matcha-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                    <ArrowUpRight className="text-neutral-500 group-hover:text-white transition-colors" width={18} height={18} />
                  </div>
                </div>
              </div>
            </a>

            {/* Secondary row - LinkedIn & GitHub side by side on larger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* LinkedIn */}
              <a
                href="https://linkedin.com/in/anthony-abid"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block"
              >
                <div className="relative p-4 bg-neutral-800/30 rounded-xl border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 group-hover:bg-neutral-800/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 bg-neutral-800 rounded-lg transition-colors duration-300 group-hover:bg-[#0A66C2]/10">
                      <Linkedin className="text-neutral-400 group-hover:text-[#0A66C2] transition-colors" width={18} height={18} />
                    </div>
                    <ArrowUpRight className="ml-auto text-neutral-700 group-hover:text-neutral-400 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" width={14} height={14} />
                  </div>
                  <div className="text-[10px] text-neutral-600 font-mono uppercase tracking-wider mb-0.5">LinkedIn</div>
                  <div className="text-neutral-300 font-medium text-sm group-hover:text-white transition-colors">anthony-abid</div>
                </div>
              </a>

              {/* GitHub */}
              <a
                href="https://github.com/AnthonyA671"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block"
              >
                <div className="relative p-4 bg-neutral-800/30 rounded-xl border border-neutral-800 transition-all duration-300 group-hover:border-neutral-600 group-hover:bg-neutral-800/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 bg-neutral-800 rounded-lg transition-colors duration-300 group-hover:bg-neutral-700">
                      <Github className="text-neutral-400 group-hover:text-white transition-colors" width={18} height={18} />
                    </div>
                    <ArrowUpRight className="ml-auto text-neutral-700 group-hover:text-neutral-400 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" width={14} height={14} />
                  </div>
                  <div className="text-[10px] text-neutral-600 font-mono uppercase tracking-wider mb-0.5">GitHub</div>
                  <div className="text-neutral-300 font-medium text-sm group-hover:text-white transition-colors">AnthonyA671</div>
                </div>
              </a>
            </div>

            {/* CTA Button - More integrated design */}
            <a
              href="mailto:anth.abid@gmail.com?subject=Let's Connect"
              className="group relative flex items-center justify-center gap-3 w-full p-4 mt-2 rounded-xl overflow-hidden transition-all duration-300"
            >
              {/* Button background with gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-matcha-600 via-matcha-500 to-matcha-600 transition-all duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-matcha-500 via-matcha-400 to-matcha-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Shine effect on hover */}
              <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              <span className="relative font-semibold text-white">Send a Message</span>
              <ArrowUpRight className="relative text-white/80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" width={18} height={18} />
            </a>
          </motion.div>
        </div>

        {/* Footer - subtle refinement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-20 pt-6 border-t border-neutral-800/50"
        >
          <p className="text-sm text-neutral-600 font-mono">
            © 2025 Anthony Abid
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
